import {
  problemCodeSchema,
  sessionActivityKindSchema,
  sessionActivityOutcomeSchema,
  taxPolicyVersionSchema,
  ufSchema,
} from "@backchannel/tax-contracts";
import { crmLeadQuerySchema } from "@backchannel/tax-contracts";
import { z } from "zod";
export const crmSearchSchema = crmLeadQuerySchema
  .omit({ q: true })
  .extend({ lead: z.string().uuid().optional() });
export type CrmSearch = z.infer<typeof crmSearchSchema>;

/**
 * URL is the single source of truth for list state.
 *
 * Every filter, sort, tab, cursor and selection round-trips through the query
 * string so an operator can paste a link into a ticket and the reviewer sees
 * exactly the same 40 rows. Multi-value filters are comma-joined rather than
 * repeated or JSON-encoded, which keeps the URL readable in a chat message:
 *
 *   /evidence?status=NEW,IN_REVIEW&jurisdiction=BR-SP&sort=fetchedAt&dir=asc
 */

/**
 * Query-string values are JSON-decoded (booleans, numbers, legacy quoted
 * strings) except digit-only strings: NCM, CEST and SH chapters are codes,
 * not numbers, so `chapter=88` stays the string "88" and is written without
 * JSON quotes (`%2288%22`). Old links with the quotes decode to the same string.
 */
export function parseSearchValue(value: string): unknown {
  if (/^\d+$/u.test(value)) throw new Error("digit-only code stays a string");
  return JSON.parse(value);
}

function decodeSearchValue(raw: string): unknown {
  try {
    return parseSearchValue(raw);
  } catch {
    return raw;
  }
}

/**
 * The router's default parser turns `ncm=17049010` into a number before any
 * custom value parser runs, so parse the query string here instead. Repeated
 * keys become arrays, as before.
 */
export function parseAppSearch(searchStr: string): Record<string, unknown> {
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const out: Record<string, unknown> = {};
  for (const [key, raw] of params) {
    const value = decodeSearchValue(raw);
    const previous = out[key];
    out[key] = previous === undefined ? value : ([] as unknown[]).concat(previous, value);
  }
  return out;
}

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDir = (typeof SORT_DIRECTIONS)[number];

export function parseCsv(value: string | undefined | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function toCsv(values: readonly string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return [...new Set(values)].join(",");
}

/** Toggles a value inside a CSV filter, returning the next CSV (or undefined). */
export function toggleCsv(current: string | undefined, value: string): string | undefined {
  const items = parseCsv(current);
  const next = items.includes(value) ? items.filter((entry) => entry !== value) : [...items, value];
  return toCsv(next);
}

const csvField = z.string().trim().optional().catch(undefined);
const textField = z.string().trim().optional().catch(undefined);
/** Mesa query: do not trim. Sequential typing needs in-progress spaces. */
const deskField = z.string().optional().catch(undefined);

/** Exact authenticated integration paths exposed in Developers request history. */
const integrationRequestRouteSchema = z.enum([
  "/v1/integrations/rulesets/current",
  "/v1/integrations/rules",
  "/v1/integrations/ncm-catalog",
  "/v1/integrations/calculations:batch",
  "/v1/integrations/calculations/{calculationId}",
  "/v1/integrations/tax-lookup-jobs",
  "/v1/integrations/tax-lookup-jobs/{jobId}",
  "/v1/integrations/tax-lookup-jobs/{jobId}/entries",
]);

/**
 * Shared list search shape. `.catch()` everywhere means a hand-edited or stale
 * URL degrades to defaults instead of throwing a router error at an operator
 * who just wanted to look at a table.
 */
export const listSearchSchema = z.object({
  q: textField,
  sort: textField,
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
  cursor: textField,
  limit: z.coerce.number().int().min(10).max(200).optional().catch(undefined),
  /** Comma-separated ids of selected rows; capped by the caller. */
  selected: csvField,
  tab: textField,
});

export type ListSearch = z.infer<typeof listSearchSchema>;

export const evidenceSearchSchema = listSearchSchema.extend({
  status: csvField,
  jurisdiction: csvField,
  tag: csvField,
  /** Mesa de descoberta query. Distinct from `q`, which still filters the queue. */
  desk: deskField,
  /** Durable fiscal research task selected from the task queue. */
  agentTask: textField,
});
export type EvidenceSearch = z.infer<typeof evidenceSearchSchema>;

/** Revisões tab for the discovery desk. Legacy `desk` / `agentTask` / #research URLs still open it. */
export function isDiscoveryEvidenceView(
  search: Pick<EvidenceSearch, "tab" | "desk" | "agentTask">,
  hash = "",
): boolean {
  if (search.tab === "discovery") return true;
  if (search.tab === "tasks" || search.tab === "documents") return false;
  const fragment = hash.replace(/^#/, "");
  return Boolean(search.desk || search.agentTask || fragment === "research" || fragment === "mesa");
}

export function discoveryDeskSearch(input: { desk?: string; agentTask?: string } = {}): {
  tab: "discovery";
  desk?: string;
  agentTask?: string;
} {
  return {
    tab: "discovery",
    ...(input.desk ? { desk: input.desk } : {}),
    ...(input.agentTask ? { agentTask: input.agentTask } : {}),
  };
}

export const proposalSearchSchema = listSearchSchema.extend({
  evidenceQuery: textField,
  create: z.boolean().optional().catch(undefined),
  ruleType: z
    .enum(["FISCAL_PARAMETER", "COMMERCIAL_FEE", "OPERATIONAL_COST"])
    .optional()
    .catch(undefined),
  status: csvField,
  author: csvField,
});
export type ProposalSearch = z.infer<typeof proposalSearchSchema>;

export const rulesetSearchSchema = listSearchSchema.extend({
  status: csvField,
  /** Guided flow state, also URL-addressable so a review can be shared. */
  flow: z.enum(["publish", "rollback"]).optional().catch(undefined),
  step: z.coerce.number().int().min(0).max(3).optional().catch(undefined),
  from: textField,
  to: textField,
  /** A policy suggested on the draft's policy panel (the proposal plan import links it). */
  policy: taxPolicyVersionSchema.optional().catch(undefined),
});
export type RulesetSearch = z.infer<typeof rulesetSearchSchema>;

export const ruleSearchSchema = listSearchSchema.extend({
  kind: csvField,
  chapter: z
    .string()
    .regex(/^\d{2}$/)
    .optional()
    .catch(undefined),
  ncm: textField,
  uf: csvField,
  originUf: z
    .string()
    .refine((value) => ufSchema.safeParse(value).success)
    .optional()
    .catch(undefined),
  destinationUf: z
    .string()
    .refine((value) => ufSchema.safeParse(value).success)
    .optional()
    .catch(undefined),
  asOf: z.iso.date().optional().catch(undefined),
  rulesetVersion: textField,
  /** Simple lookup field: search by NCM, CEST or product description. */
  by: z.enum(["ncm", "cest", "product"]).optional().catch(undefined),
  /** Legacy inspect query. The list redirects it to `/rules/$ruleId`. */
  inspect: textField,
});
export type RuleSearch = z.infer<typeof ruleSearchSchema>;

export const coverageGapSearchSchema = listSearchSchema.extend({
  status: z
    .enum(["OPEN", "RESEARCHING", "NO_RULE_FOUND", "RULE_FOUND", "RESOLVED", "DISMISSED", "ALL"])
    .optional()
    .catch(undefined),
  destinationUf: z
    .string()
    .refine((value) => ufSchema.safeParse(value).success)
    .optional()
    .catch(undefined),
  /** Gap whose finding dialog is open. */
  gap: textField,
  dialog: z.enum(["finding", "dismiss"]).optional().catch(undefined),
});
export type CoverageGapSearch = z.infer<typeof coverageGapSearchSchema>;

/** Agentes → Agente IOB tabs. "live" is the default and stays out of the URL. */
export const BROWSER_AGENT_TABS = ["live", "queue", "schedule", "history", "settings"] as const;
export type BrowserAgentTab = (typeof BROWSER_AGENT_TABS)[number];

/** Agentes → Agente IOB: tab, selected run and findings outcome filter. */
export const browserAgentSearchSchema = z.object({
  tab: z.enum(BROWSER_AGENT_TABS).optional().catch(undefined),
  run: z.string().uuid().optional().catch(undefined),
  /** Task whose detail drawer is open. */
  task: z.string().uuid().optional().catch(undefined),
  outcome: z
    .enum([
      "ST_FOUND",
      "NO_ST_FOUND",
      "INTERNAL_ST_ONLY",
      "NCM_NOT_FOUND",
      "UNREADABLE",
      "INCONCLUSIVE",
    ])
    .optional()
    .catch(undefined),
});
export type BrowserAgentSearch = z.infer<typeof browserAgentSearchSchema>;

/** Administração → Pesquisa web: tab and the sweep open in the detail panel. */
export const webResearchSearchSchema = z.object({
  tab: z.enum(["sweeps", "settings"]).optional().catch(undefined),
  sweep: z.string().uuid().optional().catch(undefined),
});
export type WebResearchSearch = z.infer<typeof webResearchSearchSchema>;

/** Administração → Autoaprovação: Etapas · Limites · Pré-visualização · Relatório. */
export const autoApprovalSearchSchema = z.object({
  tab: z.enum(["stages", "limits", "preview", "report"]).optional().catch(undefined),
});
export type AutoApprovalSearch = z.infer<typeof autoApprovalSearchSchema>;

/** Regras → Verificação: queue tab, page cursor and the fact open in the split view. */
export const fiscalVerificationSearchSchema = z.object({
  tab: z
    .enum([
      "unverified",
      "conflicting",
      "drift",
      "revalidation",
      "expired",
      "agreements",
      "nfe",
      "confirmed",
    ])
    .optional()
    .catch(undefined),
  fact: z.string().uuid().optional().catch(undefined),
  /* Notas fiscais: the observed path (drawer) or import batch (drawer) open. */
  path: z.string().uuid().optional().catch(undefined),
  batch: z.string().uuid().optional().catch(undefined),
  /* Notas fiscais filters: origin / destination UF, NCM prefix, statuses (CSV). */
  origin: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional()
    .catch(undefined),
  dest: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional()
    .catch(undefined),
  ncm: z
    .string()
    .regex(/^\d{2,8}$/)
    .optional()
    .catch(undefined),
  status: z
    .string()
    .regex(/^(MATCHES|DIVERGES|NOT_COVERED)(,(MATCHES|DIVERGES|NOT_COVERED)){0,2}$/)
    .optional()
    .catch(undefined),
  /* Notas fiscais: outcome filter of the open batch's file results. */
  result: z
    .enum(["IMPORTED", "CANCELLED", "DUPLICATE", "INVALID", "SKIPPED"])
    .optional()
    .catch(undefined),
  /* Acordos CONFAZ: the agreement observation open in the split view. */
  agreement: z.string().uuid().optional().catch(undefined),
  cursor: z.string().min(1).max(200).optional().catch(undefined),
  /* Não verificados: group review (default), segment × UF, or the fact list. */
  view: z.enum(["document", "segment", "facts"]).optional().catch(undefined),
  group: z
    .string()
    .regex(/^[ds]-[a-f0-9]{16}$/)
    .optional()
    .catch(undefined),
  q: z.string().trim().min(1).max(120).optional().catch(undefined),
  uf: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional()
    .catch(undefined),
  segment: z
    .string()
    .regex(/^\d{2}$/)
    .optional()
    .catch(undefined),
  scope: z.enum(["ALL_ORIGINS", "ORIGINS", "INTERNAL_ONLY"]).optional().catch(undefined),
  validity: z.enum(["STATED", "INFERRED"]).optional().catch(undefined),
  exceptions: z.enum(["WITH", "WITHOUT"]).optional().catch(undefined),
  sort: z.enum(["IMPACT", "AGE", "SIZE"]).optional().catch(undefined),
  /* Review desk: list filter, active field and the phone pane. */
  filter: z.enum(["all", "sample", "pending", "exceptions", "flagged"]).optional().catch(undefined),
  field: z
    .enum([
      "cest",
      "ncm",
      "originalMva",
      "destinationInternalRate",
      "interstateRate",
      "validFrom",
      "stApplicable",
    ])
    .optional()
    .catch(undefined),
  pane: z.enum(["fact", "evidence"]).optional().catch(undefined),
});
export type FiscalVerificationSearch = z.infer<typeof fiscalVerificationSearchSchema>;

/**
 * Notas fiscais (`/invoices`): the month (`YYYY-MM`), the direction filter,
 * the section of the month, and the filters of its lists. The path drawer and
 * the batch drawer are addressable too, so a link shows the same screen.
 */
export const invoicesSearchSchema = z.object({
  period: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  dir: z.enum(["EMITIDA", "RECEBIDA"]).optional().catch(undefined),
  view: z.enum(["months", "batches"]).optional().catch(undefined),
  tab: z.enum(["paths", "findings", "ai", "analysis", "history"]).optional().catch(undefined),
  /* Paths: statuses (CSV), NCM prefix, acknowledgement. */
  pstatus: z
    .string()
    .regex(/^(MATCHES|DIVERGES|NOT_COVERED)(,(MATCHES|DIVERGES|NOT_COVERED)){0,2}$/)
    .optional()
    .catch(undefined),
  pncm: z
    .string()
    .regex(/^\d{2,8}$/)
    .optional()
    .catch(undefined),
  pack: z.enum(["yes", "no"]).optional().catch(undefined),
  /* Findings: severities (CSV), acknowledgement, deterministic code. */
  fsev: z
    .string()
    .regex(/^(HIGH|MEDIUM|LOW)(,(HIGH|MEDIUM|LOW)){0,2}$/)
    .optional()
    .catch(undefined),
  fack: z.enum(["yes", "no"]).optional().catch(undefined),
  fcode: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{2,59}$/)
    .optional()
    .catch(undefined),
  cursor: z.string().min(1).max(200).optional().catch(undefined),
  path: z.string().uuid().optional().catch(undefined),
  batch: z.string().uuid().optional().catch(undefined),
  result: z
    .enum(["IMPORTED", "CANCELLED", "DUPLICATE", "INVALID", "SKIPPED"])
    .optional()
    .catch(undefined),
});
export type InvoicesSearch = z.infer<typeof invoicesSearchSchema>;

/** Registros do agente: the Brasília day on screen and its client-side filters. */
export const agentFindingsSearchSchema = z.object({
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  outcome: z
    .enum([
      "ST_FOUND",
      "NO_ST_FOUND",
      "INTERNAL_ST_ONLY",
      "NCM_NOT_FOUND",
      "UNREADABLE",
      "INCONCLUSIVE",
    ])
    .optional()
    .catch(undefined),
  route: z
    .string()
    .regex(/^[A-Z]{2}-[A-Z]{2}$/)
    .optional()
    .catch(undefined),
  ncm: z.string().max(60).optional().catch(undefined),
  window: z
    .union([z.literal(30), z.literal(90), z.literal("30"), z.literal("90")])
    .transform((value) => Number(value) as 30 | 90)
    .optional()
    .catch(undefined),
});
export type AgentFindingsSearch = z.infer<typeof agentFindingsSearchSchema>;

/**
 * Administração → Planilhas com erro (`/admin/upload-failures`). `q` is the
 * support code the operator pasted; `failure` opens its drawer, so a link in
 * a support ticket lands on exactly that upload.
 */
const isoDateField = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
export const uploadFailuresSearchSchema = listSearchSchema.extend({
  limit: z.coerce.number().int().min(10).max(100).optional().catch(undefined),
  from: isoDateField,
  to: isoDateField,
  operator: textField,
  errorCode: z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{1,63}$/)
    .optional()
    .catch(undefined),
  kind: z.enum(["xlsx", "xls", "csv", "zip", "html", "unknown"]).optional().catch(undefined),
  stage: z.enum(["INSPECT", "IMPORT", "REPAIR"]).optional().catch(undefined),
  /** Words, not booleans: the router would parse "false" and the cleaner would drop it. */
  review: z.enum(["pending", "done"]).optional().catch(undefined),
  failure: textField,
});
export type UploadFailuresSearch = z.infer<typeof uploadFailuresSearchSchema>;

/**
 * Administração → Sessões. `view` switches the list and the activity log.
 * `hasErrors` stays a word: a boolean `false` would be stripped from the URL.
 * `settings` opens the recording drawer without resetting the list.
 */
export const sessionsSearchSchema = listSearchSchema.extend({
  view: z.enum(["sessions", "activity"]).optional().catch(undefined),
  principal: z.string().trim().min(1).max(320).optional().catch(undefined),
  from: isoDateField,
  to: isoDateField,
  hasErrors: z.enum(["true", "false"]).optional().catch(undefined),
  actionKind: sessionActivityKindSchema.optional().catch(undefined),
  /** Not `outcome`: that key is the agent-findings enum on the shared router search. */
  activityOutcome: sessionActivityOutcomeSchema.optional().catch(undefined),
  organizationId: z.string().uuid().optional().catch(undefined),
  limit: z.coerce.number().int().min(25).max(100).optional().catch(undefined),
  settings: z.literal("1").optional().catch(undefined),
});
export type SessionsSearch = z.infer<typeof sessionsSearchSchema>;

/** `/admin/sessions/$replaySessionId`. `t` seeks the player on first load. */
export const sessionReplaySearchSchema = z.object({
  t: z.string().datetime({ offset: true }).optional().catch(undefined),
});
export type SessionReplaySearch = z.infer<typeof sessionReplaySearchSchema>;

export const calculationSearchSchema = listSearchSchema.extend({
  rulesetVersion: textField,
  issue: csvField,
  reviewOnly: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .transform((value) => value === true || value === "true")
    .optional()
    .catch(undefined),
  /** Item id whose explain drawer is open. */
  explain: textField,
  /** Playground fiscal snapshot id. Not a marketplace lot. */
  lot: textField,
});
export type CalculationSearch = z.infer<typeof calculationSearchSchema>;

/**
 * Lot history (`/lots`). `period` picks a rolling window; `from`/`to` are
 * calendar dates for a custom range. `job` addresses the drawer as
 * `<kind>:<jobId>` so an auditor can paste exactly what they were reading.
 */
export const lotsSearchSchema = listSearchSchema.extend({
  route: textField,
  limit: z.coerce.number().int().min(10).max(100).optional().catch(undefined),
  period: z.enum(["today", "7d", "30d", "custom"]).optional().catch(undefined),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  status: csvField,
  origin: csvField,
  reviewOnly: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .transform((value) => value === true || value === "true")
    .optional()
    .catch(undefined),
  job: textField,
});
export type LotsSearch = z.infer<typeof lotsSearchSchema>;

export const lotSimulatorSearchSchema = z.object({
  sourceCalculation: z.string().uuid().optional().catch(undefined),
  sourceItem: textField,
  legacy: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .transform((value) => value === true || value === "true")
    .optional()
    .catch(undefined),
  mode: z.enum(["lote", "individual", "operacao"]).optional().catch(undefined),
  ncm: z
    .string()
    .regex(/^\d{8}$/)
    .optional()
    .catch(undefined),
  originUf: z
    .string()
    .refine((value) => ufSchema.safeParse(value).success)
    .optional()
    .catch(undefined),
  destinationUf: z
    .string()
    .refine((value) => ufSchema.safeParse(value).success)
    .optional()
    .catch(undefined),
  asOf: z.iso.date().optional().catch(undefined),
  rulesetVersion: textField,
});
export type LotSimulatorSearch = z.infer<typeof lotSimulatorSearchSchema>;

export const fiscalTaskSearchSchema = listSearchSchema.extend({
  route: textField,
  ncm: textField,
  issue: textField,
  explain: textField,
  status: csvField,
  sourceSystem: textField,
  itemId: textField,
});
export type FiscalTaskSearch = z.infer<typeof fiscalTaskSearchSchema>;

export const auditSearchSchema = listSearchSchema.extend({
  action: csvField,
  actor: csvField,
  /** Legacy inspect query. The list redirects it to `/audit/$auditId`. */
  inspect: textField,
});
export type AuditSearch = z.infer<typeof auditSearchSchema>;

export const sourceSearchSchema = listSearchSchema.extend({
  status: csvField,
});
export type SourceSearch = z.infer<typeof sourceSearchSchema>;

/**
 * Settings has one account surface and one explicit Developers workspace.
 * Keeping the discriminator in the route schema prevents TanStack Router
 * from dropping it while still treating unknown values as the account view.
 */
export const settingsSearchSchema = z.object({
  workspace: z.literal("developers").optional().catch(undefined),
  /** Comma-separated HTTP status codes; the panel validates the values. */
  statusCode: csvField,
  integrationId: textField,
  keyId: textField,
  method: z.enum(["GET", "POST"]).optional().catch(undefined),
  route: integrationRequestRouteSchema.optional().catch(undefined),
  problemCode: problemCodeSchema.optional().catch(undefined),
  idempotencyReplayed: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .transform((value) => value === true || value === "true")
    .optional()
    .catch(undefined),
  cursor: textField,
  limit: z.coerce.number().int().min(25).max(100).optional().catch(undefined),
  inspect: textField,
});
export type SettingsSearch = z.infer<typeof settingsSearchSchema>;

/** Exact-run receipts use their own cursor and a fixed observedAt sort. */
export const sourceRunSearchSchema = z.object({
  cursor: textField,
  limit: z.coerce
    .number()
    .pipe(z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)]))
    .optional()
    .catch(undefined),
});

export const OVERVIEW_DESK_TABS = ["map", "desk"] as const;
export type OverviewDeskTab = (typeof OVERVIEW_DESK_TABS)[number];

export const overviewSearchSchema = z.object({
  tab: z.enum(OVERVIEW_DESK_TABS).optional().catch(undefined),
});
export type OverviewSearch = z.infer<typeof overviewSearchSchema>;

/**
 * Strips empty values so the address bar never accumulates `?q=&sort=` noise
 * and two logically identical views produce one cache key.
 */
export function cleanSearch<T extends Record<string, unknown>>(search: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out as Partial<T>;
}

/** Filter keys whose change must reset cursor pagination to the first page. */
export const CURSOR_RESETTING_KEYS = [
  "q",
  "sort",
  "dir",
  "limit",
  "status",
  "jurisdiction",
  "tag",
  "author",
  "kind",
  "ncm",
  "chapter",
  "uf",
  "originUf",
  "destinationUf",
  "asOf",
  "rulesetVersion",
  "issue",
  "reviewOnly",
  "sourceSystem",
  "itemId",
  "action",
  "actor",
  "tab",
  "period",
  "from",
  "to",
  "origin",
  "principal",
  "hasErrors",
  "actionKind",
  "activityOutcome",
  "organizationId",
  "view",
] as const;

export function shouldResetCursor(patch: Record<string, unknown>): boolean {
  return Object.keys(patch).some((key) =>
    (CURSOR_RESETTING_KEYS as readonly string[]).includes(key),
  );
}
